<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const searchQuery = ref('');
const selectedIds = ref([]);
const isSubmitting = ref(false);
const isLoadingMembers = ref(false);
const avatarFile = ref(null);
const avatarPreview = ref('');
const removeAvatar = ref(false);

const roomId = ref(null);
const roomName = ref('');
const currentMembers = ref([]);

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const isFetchingAgents = useMapGetter('agents/getUIFlags');

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  return (agents.value || [])
    .filter(agent => agent.confirmed)
    .filter(agent => {
      if (!query) return true;
      const name = (agent.name || '').toLowerCase();
      const email = (agent.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
});

const canSubmit = computed(
  () =>
    selectedIds.value.length > 0 &&
    !isSubmitting.value &&
    !isLoadingMembers.value
);

const resetForm = () => {
  searchQuery.value = '';
  selectedIds.value = [];
  roomId.value = null;
  roomName.value = '';
  currentMembers.value = [];
  avatarFile.value = null;
  avatarPreview.value = '';
  removeAvatar.value = false;
};

const open = async ({ id, name, avatarUrl }) => {
  resetForm();
  roomId.value = id;
  roomName.value = name || '';
  avatarPreview.value = avatarUrl || '';

  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }

  isLoadingMembers.value = true;
  dialogRef.value?.open();
  try {
    const members = await store.dispatch('internalRooms/fetchMembers', id);
    currentMembers.value = members;
    selectedIds.value = members.map(m => Number(m.user_id));
  } finally {
    isLoadingMembers.value = false;
  }
};

const close = () => {
  dialogRef.value?.close();
};

const toggleAgent = agentId => {
  const id = Number(agentId);
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter(x => x !== id);
  } else {
    selectedIds.value = [...selectedIds.value, id];
  }
};

const isSelected = agentId => selectedIds.value.includes(Number(agentId));
const isCurrentUser = agentId => Number(agentId) === currentUser.value?.id;

const onAvatarChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  avatarFile.value = file;
  removeAvatar.value = false;
  const reader = new FileReader();
  reader.onload = e => {
    avatarPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const onRemoveAvatar = () => {
  avatarFile.value = null;
  avatarPreview.value = '';
  removeAvatar.value = true;
};

const onConfirm = async () => {
  if (!canSubmit.value || !roomId.value) return;
  isSubmitting.value = true;
  try {
    if (removeAvatar.value) {
      await store.dispatch('internalRooms/deleteRoomAvatar', roomId.value);
    } else if (avatarFile.value) {
      await store.dispatch('internalRooms/updateRoom', {
        roomId: roomId.value,
        avatar: avatarFile.value,
      });
    }

    await store.dispatch('internalRooms/syncMembers', {
      roomId: roomId.value,
      userIds: selectedIds.value,
      currentMembers: currentMembers.value,
    });
    emit('saved', { roomId: roomId.value });
    close();
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('INTERNAL_CHAT.GROUP_SETTINGS_TITLE')"
    :description="roomName || t('INTERNAL_CHAT.EDIT_MEMBERS_DESCRIPTION')"
    :confirm-button-label="t('INTERNAL_CHAT.SAVE_MEMBERS')"
    :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting || isLoadingMembers"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <div>
        <label class="block text-sm font-medium mb-2">
          {{ t('INTERNAL_CHAT.GROUP_PHOTO') }}
        </label>
        <div class="flex items-center gap-4">
          <Avatar
            :name="roomName"
            :src="avatarPreview"
            :size="64"
            :rounded-full
          />
          <div class="flex flex-col gap-2">
            <label
              class="px-3 py-1.5 text-xs rounded-lg border border-n-brand text-n-brand hover:bg-n-alpha-1 cursor-pointer w-fit"
            >
              {{ t('INTERNAL_CHAT.UPLOAD_PHOTO') }}
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                class="hidden"
                @change="onAvatarChange"
              />
            </label>
            <button
              v-if="avatarPreview"
              type="button"
              class="px-3 py-1.5 text-xs rounded-lg border border-n-weak hover:bg-n-alpha-1 w-fit text-n-ruby-11"
              @click="onRemoveAvatar"
            >
              {{ t('INTERNAL_CHAT.REMOVE_PHOTO') }}
            </button>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">
          {{ t('INTERNAL_CHAT.GROUP_MEMBERS') }}
        </label>
        <input
          v-model="searchQuery"
          type="text"
          class="w-full rounded-lg border border-n-weak px-3 py-2 bg-n-background text-sm mb-2"
          :placeholder="t('INTERNAL_CHAT.SEARCH_AGENTS')"
        />
        <div
          v-if="isLoadingMembers || isFetchingAgents.isFetching"
          class="text-sm text-n-slate-11 py-4 text-center"
        >
          {{ t('INTERNAL_CHAT.LOADING') }}
        </div>
        <div
          v-else-if="!filteredAgents.length"
          class="text-sm text-n-slate-11 py-4 text-center"
        >
          {{ t('INTERNAL_CHAT.NO_AGENTS') }}
        </div>
        <ul v-else class="flex flex-col gap-1 max-h-60 overflow-y-auto">
          <li v-for="agent in filteredAgents" :key="agent.id">
            <label
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-2 cursor-pointer"
            >
              <input
                type="checkbox"
                class="rounded border-n-weak"
                :checked="isSelected(agent.id)"
                @change="toggleAgent(agent.id)"
              />
              <Avatar
                :name="agent.name || agent.email"
                :src="agent.thumbnail"
                :size="32"
                :rounded-full
              />
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium truncate">
                  {{ agent.name }}
                  <span
                    v-if="isCurrentUser(agent.id)"
                    class="text-n-slate-11 font-normal"
                  >
                    ({{ t('INTERNAL_CHAT.YOU') }})
                  </span>
                </span>
                <span class="block text-xs text-n-slate-11 truncate">{{
                  agent.email
                }}</span>
              </span>
            </label>
          </li>
        </ul>
        <p v-if="selectedIds.length" class="text-xs text-n-slate-11 mt-2">
          {{ t('INTERNAL_CHAT.SELECTED_COUNT', { count: selectedIds.length }) }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
